import { Dropdown, Tag, TextField } from '@lego/connect-components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REGEX_EXAMPLES } from '@/constants/regex-presets';
import type { FormVariableState } from '@/types/types';
import { FieldGroup } from './formVariableUtils';

const CUSTOM_REGEX = 'custom';

const RegexTestIndicator = ({ pattern }: { pattern: string }) => {
  const { t } = useTranslation();
  const [testValue, setTestValue] = useState('');

  let isValidRegex = true;
  let matches = false;

  try {
    const regex = new RegExp(pattern);
    matches = testValue.length > 0 && regex.test(testValue);
  } catch {
    isValidRegex = false;
  }

  return (
    <div className="flex flex-col items-start gap-100">
      <TextField
        size="medium"
        placeholder={t('formPage.variableEditor.testPatternPlaceholder')}
        value={testValue}
        onChange={(_e, value) => setTestValue(value)}
      />
      {!isValidRegex && (
        <Tag size="small" variant="negative">
          {t('formPage.variableEditor.invalidRegex')}
        </Tag>
      )}
      {isValidRegex && testValue.length > 0 && (
        <Tag
          size="small"
          variant={matches ? 'positive' : 'negative'}
          iconName={matches ? 'control-check' : 'control-close'}
        >
          {matches
            ? t('formPage.variableEditor.regexMatch')
            : t('formPage.variableEditor.regexNoMatch')}
        </Tag>
      )}
    </div>
  );
};

export const FormVariableRegex = ({
  variable,
  onChange,
  portalTarget,
}: {
  variable: FormVariableState;
  onChange: (patch: Partial<FormVariableState>) => void;
  portalTarget?: Element | null;
}) => {
  const { t } = useTranslation();
  const [isCustom, setIsCustom] = useState(false);
  const selectedExample = REGEX_EXAMPLES.find(({ pattern }) => pattern === variable.variableRule);
  const showCustom = isCustom || (!!variable.variableRule && !selectedExample);
  const selectedValue = showCustom ? CUSTOM_REGEX : selectedExample?.pattern;
  return (
    <FieldGroup
      label={t('formPage.variableEditor.validationRegex')}
      tooltip={t('formPage.variableEditor.regexHelpText')}
    >
      <Dropdown
        fullWidth
        value={selectedValue ? [selectedValue] : []}
        placeholder={t('formPage.variableEditor.selectRegex')}
        showClearButton
        {...(portalTarget ? { portalTarget } : {})}
        onChange={(_e, items) => {
          const value = items[0]?.value;
          setIsCustom(value === CUSTOM_REGEX);
          if (value !== CUSTOM_REGEX) onChange({ variableRule: value ?? '' });
        }}
      >
        {REGEX_EXAMPLES.map(({ pattern, key }) => (
          <Dropdown.Option key={key} value={pattern} subtext={pattern}>
            {t(`formPage.variableEditor.regexExamples.${key}`)}
          </Dropdown.Option>
        ))}
        <Dropdown.Option value={CUSTOM_REGEX}>
          {t('formPage.variableEditor.customRegex')}
        </Dropdown.Option>
      </Dropdown>
      {showCustom && (
        <div className="mt-150">
          <TextField
            size="medium"
            placeholder={t('formPage.variableEditor.validationRegexPlaceholder')}
            value={variable.variableRule}
            onChange={(_e, value) => onChange({ variableRule: value })}
          />
        </div>
      )}
      {variable.variableRule.trim() && (
        <div className="mt-150">
          <RegexTestIndicator pattern={variable.variableRule} />
        </div>
      )}
    </FieldGroup>
  );
};
